import React, { createContext, useContext, useState, useEffect } from 'react';

interface UpdateContextType {
  updateAvailable: boolean;
  checkForUpdates: () => void;
}

const UpdateContext = createContext<UpdateContextType | undefined>(undefined);

export const UpdateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkForUpdates = () => {
    // package.json의 현재 버전을 가져옴
    const appVersion = process.env.REACT_APP_VERSION || '1.0.0';
    
    fetch('/meta.json?' + new Date().getTime())  // 캐시 방지를 위한 타임스탬프 추가
      .then(response => response.json())
      .then(meta => {
        const currentVersion = localStorage.getItem('app-version') || appVersion;
        
        // 서버의 버전이 더 최신인 경우에만 업데이트 알림
        if (currentVersion !== meta.version && isNewerVersion(meta.version, currentVersion)) {
          setUpdateAvailable(true);
          localStorage.setItem('app-version', meta.version);
        }
      })
      .catch(error => {
        console.error('Update check failed:', error);
      });
  };

  // 버전 비교 함수
  const isNewerVersion = (newVersion: string, oldVersion: string) => {
    const newParts = newVersion.split('.').map(Number);
    const oldParts = oldVersion.split('.').map(Number);
    
    for (let i = 0; i < 3; i++) {
      if (newParts[i] > oldParts[i]) return true;
      if (newParts[i] < oldParts[i]) return false;
    }
    return false;
  };

  useEffect(() => {
    // 주기적으로 업데이트 체크 (5분마다)
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    // 초기 로드시 한 번 체크
    checkForUpdates();

    return () => clearInterval(interval);
  }, []);

  return (
    <UpdateContext.Provider value={{ updateAvailable, checkForUpdates }}>
      {children}
    </UpdateContext.Provider>
  );
};

export const useUpdate = () => {
  const context = useContext(UpdateContext);
  if (context === undefined) {
    throw new Error('useUpdate must be used within an UpdateProvider');
  }
  return context;
};
